import type { KeyboardEvent } from 'react';
import { Aircraft } from '../../types';
import { createAircraft } from '../../api';

const preventEnterSubmit = (event: KeyboardEvent<HTMLInputElement>) => {
  if (event.key === 'Enter') event.preventDefault();
};

export const todayIso = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export type AircraftForm = {
  tail_number: string;
  model: string;
  purchase_date: string;
  condition: 'new' | 'used';
  airframe_hours: string;
  engine_hours: string;
  tach_at_purchase: string;
  base_airfield: string;
  maintenance_type: '' | 'technician' | 'organization' | 'self';
  maintenance_name: string;
};

export const emptyAircraftForm = (): AircraftForm => ({
  tail_number: '',
  model: '',
  purchase_date: todayIso(),
  condition: 'new',
  airframe_hours: '',
  engine_hours: '',
  tach_at_purchase: '',
  base_airfield: '',
  maintenance_type: '',
  maintenance_name: '',
});

const hoursText = (value?: number | null) =>
  value == null || !Number.isFinite(Number(value)) ? '' : String(value);

const roundHours = (value: number) => Math.round(value * 100) / 100;

export const hoursAtPurchase = (
  current?: number | null,
  journal?: number | null,
  hobbs?: number | null,
  engine?: number | null
) => {
  const cur = Number(current) || 0;
  const flown = Number(journal) || 0;
  const missing = Math.max(0, roundHours((Number(hobbs) || 0) - (Number(engine) || 0)));
  return hoursText(roundHours(cur - flown + missing));
};

export const formFromAircraft = (item: Aircraft): AircraftForm => ({
  tail_number: item.tail_number || '',
  model: item.model || '',
  purchase_date: (item.purchase_date || todayIso()).slice(0, 10),
  condition: item.condition === 'used' ? 'used' : 'new',
  airframe_hours: hoursAtPurchase(
    item.airframe_hours,
    item.journal_hours,
    item.hobbs_current,
    item.engine_hours
  ),
  engine_hours: hoursAtPurchase(
    item.engine_hours,
    item.journal_hours,
    item.hobbs_current,
    item.engine_hours
  ),
  tach_at_purchase: hoursText(item.tach_at_purchase),
  base_airfield: item.base_airfield || '',
  maintenance_type:
    item.maintenance_type === 'technician' ||
    item.maintenance_type === 'organization' ||
    item.maintenance_type === 'self'
      ? item.maintenance_type
      : '',
  maintenance_name: item.maintenance_name || '',
});

export const parseUsedHours = (form: AircraftForm) => {
  const airframe = Number(String(form.airframe_hours).replace(',', '.'));
  const engine = Number(String(form.engine_hours).replace(',', '.'));
  const tach = Number(String(form.tach_at_purchase).replace(',', '.'));
  return { airframe, engine, tach };
};

export const aircraftFormError = (
  form: AircraftForm,
  options?: { requireHours?: boolean }
): string | null => {
  if (!form.tail_number.trim()) return 'Укажите регистрационный номер';
  if (!form.purchase_date) return 'Укажите дату покупки';
  if (form.condition === 'used' || options?.requireHours) {
    const { airframe, engine, tach } = parseUsedHours(form);
    if (![airframe, engine, tach].every((n) => Number.isFinite(n) && n >= 0)) {
      return 'Укажите налёт планера, двигателя и показания первого тахометра';
    }
  }
  return null;
};

export const aircraftFormPayload = (
  form: AircraftForm,
  options?: { includeHours?: boolean }
): Parameters<typeof createAircraft>[0] => {
  const payload: Parameters<typeof createAircraft>[0] = {
    tail_number: form.tail_number.trim(),
    model: form.model.trim() || undefined,
    purchase_date: form.purchase_date,
    condition: form.condition,
    base_airfield: form.base_airfield.trim() || null,
    maintenance_type: form.maintenance_type || null,
    maintenance_name: form.maintenance_type
      ? form.maintenance_name.trim() || null
      : null,
  };
  if (form.condition === 'used' || options?.includeHours) {
    const { airframe, engine, tach } = parseUsedHours(form);
    payload.airframe_hours = airframe;
    payload.engine_hours = engine;
    payload.tach_at_purchase = tach;
  }
  return payload;
};

type Props = {
  form: AircraftForm;
  onChange: (next: AircraftForm) => void;
  disabled?: boolean;
};

export default function ClubAircraftForm({
  form,
  onChange,
  disabled,
}: Props) {
  const set = (patch: Partial<AircraftForm>) => onChange({ ...form, ...patch });

  return (
    <>
      <label className="club-field">
        <span>Регистрационный номер</span>
        <input
          value={form.tail_number}
          onChange={(e) => set({ tail_number: e.target.value })}
          maxLength={10}
          required
          placeholder="RA-1234G"
          disabled={disabled}
        />
        <small className="club-field-hint">
          Может меняться в процессе эксплуатации
        </small>
      </label>
      <label className="club-field">
        <span>Модель</span>
        <input
          value={form.model}
          onChange={(e) => set({ model: e.target.value })}
          maxLength={100}
          placeholder="Cessna 172"
          disabled={disabled}
        />
      </label>
      <label className="club-field">
        <span>Аэродром базирования</span>
        <input
          value={form.base_airfield}
          onChange={(e) => set({ base_airfield: e.target.value })}
          maxLength={120}
          placeholder="Боровой"
          disabled={disabled}
        />
        <small className="club-field-hint">
          Место заправки в «добавление полёта»
        </small>
      </label>
      <label className="club-field">
        <span>Базовая организация ТО</span>
        <select
          value={form.maintenance_type}
          disabled={disabled}
          onChange={(e) =>
            set({
              maintenance_type: e.target
                .value as AircraftForm['maintenance_type'],
            })
          }
        >
          <option value="">Не указано</option>
          <option value="technician">Техник</option>
          <option value="organization">Организация</option>
          <option value="self">Своими силами</option>
        </select>
      </label>
      {form.maintenance_type === 'technician' ||
      form.maintenance_type === 'organization' ||
      form.maintenance_type === 'self' ? (
        <label className="club-field">
          <span>
            {form.maintenance_type === 'organization'
              ? 'Наименование организации'
              : 'ФИО'}
          </span>
          <input
            value={form.maintenance_name}
            onChange={(e) => set({ maintenance_name: e.target.value })}
            maxLength={200}
            disabled={disabled}
          />
        </label>
      ) : null}
      <label className="club-field">
        <span>Дата покупки</span>
        <input
          type="date"
          value={form.purchase_date}
          max={todayIso()}
          onChange={(e) => set({ purchase_date: e.target.value })}
          required
          disabled={disabled}
        />
      </label>
      <fieldset className="club-condition">
        <legend>Состояние</legend>
        <label>
          <input
            type="radio"
            name="club-aircraft-condition"
            checked={form.condition === 'new'}
            onChange={() => set({ condition: 'new' })}
            disabled={disabled}
          />
          Новый
        </label>
        <label>
          <input
            type="radio"
            name="club-aircraft-condition"
            checked={form.condition === 'used'}
            onChange={() => set({ condition: 'used' })}
            disabled={disabled}
          />
          Б/у
        </label>
      </fieldset>

      {form.condition === 'new' ? (
        <p className="club-form-note">
          Для нового борта отсчёт ТО начнётся с формы ТО-50 (стартовое ТО-100
          проводится автоматически).
        </p>
      ) : null}

      {form.condition === 'used' ? (
        <>
          <label className="club-field">
            <span>Налёт планера на дату покупки, м/ч</span>
            <input
              type="text"
              inputMode="decimal"
              autoComplete="off"
              value={form.airframe_hours}
              onChange={(e) => set({ airframe_hours: e.target.value })}
              onKeyDown={preventEnterSubmit}
              required
              disabled={disabled}
            />
          </label>
          <label className="club-field">
            <span>Налёт двигателя на дату покупки, м/ч</span>
            <input
              type="text"
              inputMode="decimal"
              autoComplete="off"
              value={form.engine_hours}
              onChange={(e) => set({ engine_hours: e.target.value })}
              onKeyDown={preventEnterSubmit}
              required
              disabled={disabled}
            />
          </label>
          <label className="club-field">
            <span>Показания первого тахометра</span>
            <input
              type="text"
              inputMode="decimal"
              autoComplete="off"
              value={form.tach_at_purchase}
              onChange={(e) => set({ tach_at_purchase: e.target.value })}
              onKeyDown={preventEnterSubmit}
              required
              disabled={disabled}
            />
          </label>
        </>
      ) : null}
    </>
  );
}
